// ── what a session recording must never contain ──────────────────────────────
//
// /privacy promises, in as many words: "your memory is never captured — not
// dimensions, not nodes, not captures or artifacts, not anything you or the
// agent wrote". This file is the machinery behind that sentence, and it lives
// apart from instrumentation-client.ts for one reason: that module calls
// posthog.init() on import, so nothing can read it without starting analytics.
// A promise this load-bearing should be testable without side effects.
//
// WHY BLOCKING, NOT MASKING. Masking replaces text nodes. rrweb still
// serialises DOM ATTRIBUTES verbatim, and the OS puts memory into them:
// `data-node-name` is a dimension's name, `data-node-path` is where it lives,
// and `data-hint` carries its SUMMARY (components/os/DirectoryView.tsx,
// OrbitView.tsx). All three are read at runtime — drag-and-drop, the Noticer,
// the status corner — so they cannot be dropped from the markup. A BLOCKED
// element is replaced by a same-sized placeholder before serialisation, so
// neither its attributes nor its children are ever recorded.
//
// What survives: layout, scroll, cursor, clicks, and every piece of chrome.
// You can still watch someone stall on a step; you cannot read what they were
// stalling over. Which features they used is answered by the named events in
// ./events.ts, not by reading their screen.

/**
 * Selectors whose subtree never enters a recording.
 *
 * `[data-node-name]` is an attribute selector on purpose: anything that ever
 * carries a memory name is covered the day it is added, without someone
 * remembering to come back here.
 */
export const MEMORY_SURFACES: readonly string[] = [
  '.ph-no-capture', // the manual escape hatch, for anything not listed here
  '[data-node-name]', // directory rows, grid tiles, orbit tiles — name · path · summary
  '.list-name', // a node's name, wherever it renders
  '.list-sum', // and its summary
  '.orbit-tile-name',
  '.art-prose', // an artifact's body
  '.art-doc__name', // and its title
  '.nchat-stream', // netti chat — what they asked AND what it answered
  '.nchat-input',
  '.os-search-results', // search hits are memory by definition
  '.insight-dots', // generated insights name the memory they are about
  '.yo-root', // the you space — the day digest, the mirror, the private view
  '.eth-root', // the gradient modal — a project's story, chapter by chapter
  '.st-root', // project stories — the you story tab and the projects-level view
]

/** the memory half of the block list, on its own — what /privacy promises */
export const MEMORY_BLOCK_SELECTOR = MEMORY_SURFACES.join(', ')

/**
 * Surfaces blocked for COST, not content.
 *
 * ⚠️ A LIVE WEBGL CANVAS MUST NEVER ENTER A RECORDING. rrweb's canvas capture
 * does not take a picture of a WebGL canvas — it patches the context and
 * serialises EVERY GL CALL, per frame, uniforms and buffers included. On the
 * organism (a single-pass shader repainting at 60fps) that is a payload the
 * page cannot keep up with. Measured on production, signed in and in the
 * replay sample: a 16.3-second long task three seconds after the page became
 * interactive, then 6.8s and 4.8s more, two replay uploads of 31s and 20s to
 * /ingest/s/, and the load event firing at 54 seconds. The tab reads as hung;
 * a navigation cannot commit; the loading frame stays on screen. A visitor
 * outside the sample saw none of it, which is why it took a signed-in founder
 * to notice. Blocked, the canvas is a placeholder box in the replay — the
 * shape of the page is still there, which is all a replay is for.
 */
export const COST_SURFACES: readonly string[] = ['canvas']

/** The one string rrweb tests every element against. */
export const REPLAY_BLOCK_SELECTOR = [...MEMORY_SURFACES, ...COST_SURFACES].join(', ')

/**
 * Attributes a recording keeps only as asterisks, the way `maskTextSelector`
 * keeps text (scrubEvent `maskReplayAttribute`).
 *
 * Blocking keeps memory ROWS out; this is for the chrome that carries memory in
 * an attribute outside them: the navigator's words and the stat line put a
 * summary in `data-hint`, the menu a project's name; `title` and `aria-label`
 * name what a control acts on ("open <skill>", "search inside <project>", a
 * thread's preview, "kept by <person>"). rrweb records attributes verbatim, so
 * text masking never reaches them.
 */
export const REPLAY_WORD_ATTRIBUTES: readonly string[] = [
  'data-hint',
  'data-node-name',
  'title',
  'aria-label',
  'alt',
  'placeholder',
]

/**
 * Attributes holding a node's key (lib/os/tree), which is made of the same
 * words as its path (`dim:<title-slug>`): kept as its kind only (placePath
 * `templateNodeKey`). `data-nav-key` is on every navigator word, outside the
 * block list. A `data-*` attribute holding a path (`data-node-path`) needs no
 * entry: any one whose value is a path is templated as a shell path.
 */
export const REPLAY_KEY_ATTRIBUTES: readonly string[] = ['data-node-key', 'data-nav-key']

/**
 * Attributes autocapture must never ship.
 *
 * Session replay and autocapture are two different pipes to the same place. It
 * is no use blocking an element from the recording if a click on it still
 * posts its `data-node-name` as an event property — the strict half of the
 * system undone by the casual half.
 */
export const AUTOCAPTURE_ATTRIBUTE_DENYLIST: readonly string[] = [
  'data-node-name',
  'data-node-path',
  'data-hint',
  'title',
  'alt',
  'aria-label',
  'placeholder',
  'value',
]
